import { Avatar, Body, Tooltip } from '@assembly-js/design-system';
import { IconButton } from '@material-ui/core';
import moment from 'moment';
import React, { useCallback } from 'react';
import remarkIns from 'remark-ins';
import {
  Attachment,
  defaultAllowedTagNames,
  MessageErrorIcon,
  renderText,
  useMessageContext,
  useTranslationContext,
} from 'stream-chat-react';
import { makeStyles } from 'tss-react/mui';

import { CloseIcon } from 'src/components/icons';
import { ActionItem } from 'src/features/assistant/action-item/ActionItem';
import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import { CustomBlockQuoteComponent } from 'src/features/channel/channel-content/message-content/CustomBlockQuoteComponent';
import { CustomBrRenderer } from 'src/features/channel/channel-content/message-content/CustomBrRenderer';
import {
  CustomLiComponent,
  CustomOlComponent,
  CustomUlComponent,
} from 'src/features/channel/channel-content/message-content/CustomListComponents';
import { CustomMentionComponent } from 'src/features/channel/channel-content/message-content/CustomMentionComponent';
import {
  CustomTableRenderer,
  CustomTbodyRenderer,
  CustomTdRenderer,
  CustomTheadRenderer,
  CustomThRenderer,
  CustomTrRenderer,
} from 'src/features/channel/channel-content/message-content/CustomTableRenderer';
import { CustomAttachment } from 'src/features/channel/custom-attachments';
import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import history from 'src/history';
import { useContextBar } from 'src/hooks/useContextBar';
import { useFilePreview } from 'src/hooks/useFilePreview';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useAppSelector } from 'src/hooks/useStore';
import { FilePreviewDialog } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/FilePreviewDialog';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BlackHeadings, GraySmall, white } from 'src/theme/colors';
import { DateUtils } from 'src/utils';
import { addCustomCopyBehavior } from 'src/utils/ClipboardUtils';
import { getInitials, GetProfileFromChannelMember } from 'src/utils/UserUtils';

import { useChatClient } from './useChatClient';

const useStyles = makeStyles()((theme) => ({
  messageHeaderWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  messageMetaRendererRoot: {
    paddingTop: theme.spacing(0.5),
  },
  flexStartColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  flexEndRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'row',
  },
  closePreview: {
    position: 'absolute',
    top: 0,
    maxWidth: 400,
    width: '100%',
  },
  attachmentsContainer: {
    position: 'relative',
  },
  iconContainer: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 8,
    right: 8,
    padding: 5,
    backgroundColor: '#0000004D',
    '&:hover': {
      backgroundColor: '#0000004D',
    },
  },
  closeIcon: {
    color: white,
    fontSize: 10,
  },
}));

const MessageMetaInfoWrapperWidth = '60px';
const MessagePreviewMetaInfoWrapperWidth = '40px';
export const useMessageComponents = () => {
  const chatClient = useChatClient();
  const { classes } = useStyles();
  const { t, userLanguage } = useTranslationContext('MessageTeam');
  const isClient = useAppSelector((state) => state.user.isClient);

  const { isSidebarTheme } = useMessagingTheme();
  const { isInternalChat } = useInternalChatContext();
  const { target, setOpen } = useContextBar();
  const entityType = target?.entityType;
  const entityId = target?.id;
  const isMobile = useMediaQuery('xs', 'max');
  const closeSidebar = useCallback(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile, setOpen]);

  const {
    message,
    groupStyles,
    initialMessage,
    handleAction,
    unsafeHTML,
    handleRetry,
    isMyMessage,
  } = useMessageContext();
  const createdAt = message.created_at;

  const messageTextToRender =
    message.i18n?.[`${userLanguage}_text`] || message.text;
  const messageMentionedUsersItem = message.mentioned_users;
  const handleDismissLinkPreview = async (index: number) => {
    try {
      const updatedAttachments = [...(message.attachments ?? [])];
      updatedAttachments.splice(index, 1);

      await chatClient?.partialUpdateMessage(message.id, {
        set: {
          attachments: updatedAttachments,
        },
      });
    } catch (error) {
      console.error('Error dismissing link preview:', error);
    }
  };

  const messageText = React.useMemo(() => {
    if (!messageTextToRender) return null;

    // Use GetStream's built-in markdown parsing with custom renderers
    // Include heading tags (h1-h6) which are not in defaultAllowedTagNames
    // Include remark-ins plugin to support ++underline++ syntax
    return renderText(messageTextToRender, messageMentionedUsersItem, {
      allowedTagNames: [...defaultAllowedTagNames, 'ins'],
      getRehypePlugins: (defaultPlugins) => defaultPlugins,
      getRemarkPlugins: (defaultPlugins) => [...defaultPlugins, remarkIns],
      customMarkDownRenderers: {
        mention: CustomMentionComponent,
        ul: CustomUlComponent,
        ol: CustomOlComponent,
        li: CustomLiComponent,
        blockquote: CustomBlockQuoteComponent,
        table: CustomTableRenderer,
        thead: CustomTheadRenderer,
        tbody: CustomTbodyRenderer,
        tr: CustomTrRenderer,
        th: CustomThRenderer,
        td: CustomTdRenderer,
        // replace break tags with empty paragraphs to avoid extra spacing
        // in the message content. This is a workaround to avoid the issue
        // where if two successive paragraphs are separated by a break tag,
        // the br is causing so much spacing between the paragraphs.
        // Same for adjacent lists. For instance, this follow the same
        // pattern as br are rendered within the message input editor
        // break line are rendered as empty paragraphs.
        br: CustomBrRenderer,
      },
    });
  }, [messageMentionedUsersItem, messageTextToRender]);
  const firstGroupStyle = groupStyles ? groupStyles.at(0) : 'single';
  const isNewMessageGroup =
    firstGroupStyle === 'top' || firstGroupStyle === 'single' || initialMessage;

  const avatarRenderer = () => {
    const messageSenderProfile = GetProfileFromChannelMember(message.user);

    return (
      <div {...addCustomCopyBehavior({ excludeFromCopy: true })}>
        <Avatar
          size={isSidebarTheme ? 'sm' : 'lg'}
          text={getInitials(
            `${messageSenderProfile.firstName ?? ''} ${messageSenderProfile.lastName ?? ''}`,
            { twoLetters: true },
          )}
          fallbackColor={messageSenderProfile.fallbackColor ?? ''}
          src={messageSenderProfile.avatarUrl ?? ''}
          variant="circle"
        />
      </div>
    );
  };

  /**
   * This function formats the message timestamp
   * in two formats:
   * (humanized format e.g. Today at 3:00pm)
   * (only time format e.g. 17:00)
   *
   */
  const getFormattedMessageTimeStamp = React.useCallback(
    (options: { humanizeDate: boolean }) => {
      const { humanizeDate } = options;
      if (humanizeDate) {
        return DateUtils.humanizeDate(createdAt?.toString(), {
          lastWeek: 'h:mm A',
          sameElse: 'h:mm A',
        });
      }
      return DateUtils.formatDate(createdAt?.toString() || '', 'HH:mm');
    },
    [createdAt],
  );

  const MessageTimeStampRenderer = (
    humanizeDate: boolean,
    showOnHover = false,
  ) => (
    <BaseTypography
      className={showOnHover ? 'messageTimestampText' : ''}
      style={{ color: GraySmall }}
    >
      {getFormattedMessageTimeStamp({ humanizeDate })}
    </BaseTypography>
  );

  const InlineTimestamp = () => (
    <div
      className="select-none"
      {...addCustomCopyBehavior({ excludeFromCopy: true })}
    >
      {MessageTimeStampRenderer(false, true)}
    </div>
  );

  const MessageMetaRenderer = (isMessagePreview = false) => (
    <div
      className={`${classes.messageMetaRendererRoot} ${classes.flexStartColumn}`}
      style={{
        minWidth: isSidebarTheme
          ? '40px'
          : isMessagePreview
            ? MessagePreviewMetaInfoWrapperWidth
            : MessageMetaInfoWrapperWidth,
      }}
    >
      <div className={classes.flexEndRow}>
        {isMessagePreview ? (
          avatarRenderer()
        ) : (
          <>
            {isNewMessageGroup && avatarRenderer()}
            {!isNewMessageGroup && (
              <>
                {isSidebarTheme ? (
                  <InlineTimestamp />
                ) : (
                  <Tooltip
                    position="top"
                    content={MessageTimeStampRenderer(false)}
                  >
                    <InlineTimestamp />
                  </Tooltip>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  const MessageHeaderRenderer = () => (
    <div className={classes.messageHeaderWrapper}>
      <BaseTypography
        fontType="15semiBold"
        style={{ marginRight: 8, color: BlackHeadings }}
      >
        {message.user?.name}
      </BaseTypography>
      {MessageTimeStampRenderer(true)}
    </div>
  );

  const isCloseIconVisible = useCallback(
    (index: number) => {
      // Don't show icon for file_id attachments
      if (message.attachments?.[index]?.type === 'file_id') return false;
      // Show icons if there are multiple attachments
      if ((message.attachments ?? []).length > 1) return true;
      // Show icon if there's text with the attachment
      if (message.text) return true;
      // Don't show icon for single attachment without text
      return false;
    },
    [message.attachments, message.text],
  );

  const {
    previewingFile,
    previewingDocuments,
    setPreviewingFile,
    closePreview,
  } = useFilePreview();

  const handleGoToFilesTab = useCallback(() => {
    if (entityType === 'client') {
      history.replace(`/clients/users/details/${entityId}/files`);
    } else if (entityType === 'company') {
      history.replace(`/companies/${entityId}/files`);
    }
    closeSidebar();

    // Close the preview after navigation
    closePreview();
  }, [closePreview, closeSidebar, entityType, entityId]);

  const MessageContentRenderer = () => (
    <>
      <div
        className="str-chat__message-team-content ph-no-capture"
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          width: '100%',
        }} // override css specificity for message-team-content
      >
        {/* str-chat__message-team-content-text: Message Content Text */}
        {unsafeHTML && message.html ? (
          <div dangerouslySetInnerHTML={{ __html: message.html }} />
        ) : (
          messageText
        )}

        {message.status === 'failed' && (
          <button
            type="button"
            className="str-chat__message-team-failed"
            data-testid="message-team-failed"
            onClick={
              message.errorStatusCode !== 403
                ? () => handleRetry(message)
                : undefined
            }
          >
            <MessageErrorIcon />
            {message.errorStatusCode !== 403
              ? t('Message Failed · Click to try again')
              : t('Message Failed · Unauthorized')}
          </button>
        )}
      </div>
      {isInternalChat ? (
        <>
          <CustomAttachment
            setPreviewingFile={setPreviewingFile}
            actionHandler={handleAction}
            attachments={message.attachments ?? []}
          />
          {previewingFile && (
            <FilePreviewDialog
              open={previewingFile !== undefined}
              documents={previewingDocuments}
              onClose={closePreview}
              handleGoToFilesTab={target ? handleGoToFilesTab : undefined}
            />
          )}
        </>
      ) : (
        (message.attachments ?? []).length > 0 &&
        !message.isLinkPreviewDismissed && (
          <div className={classes.attachmentsContainer}>
            {(message.attachments ?? []).map((attachment, index) => (
              <div key={index} className="relative">
                <a
                  href={attachment.title_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-inherit cursor-pointer no-underline"
                >
                  <Attachment
                    actionHandler={handleAction}
                    attachments={[attachment]}
                  />
                </a>
                {!isClient && isMyMessage() && isCloseIconVisible(index) && (
                  <div className={classes.closePreview}>
                    <IconButton
                      className={classes.iconContainer}
                      onClick={() => handleDismissLinkPreview(index)}
                    >
                      <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
      {message.created_at.getTime() !== message.updated_at.getTime() && (
        <span
          className="flex select-none"
          {...addCustomCopyBehavior({ excludeFromCopy: true })}
        >
          <Tooltip
            content={
              <Body size="sm">
                {moment(message.updated_at).calendar({
                  lastWeek: 'MMM D [at] h:mm A',
                  sameElse: 'MMM D [at] h:mm A',
                })}
              </Body>
            }
            position="top"
          >
            <Body className="text-gray-small">(edited)</Body>
          </Tooltip>
        </span>
      )}
      <ActionItem ctas={message?.ctas ?? []} />
    </>
  );

  return {
    MessageMetaRenderer,
    MessageHeaderRenderer,
    MessageContentRenderer,
    isNewMessageGroup,
    avatarRenderer,
  };
};
